<template>
  <div class="hotel-form">
    <toolbar
      backRoute="/dashboard/hotels"
      :beforeBack="checkForChanges"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="hotel-item-delete"
      saveButtonLabel="Save hotel"
      saveButtonEvent="hotel-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="hotel-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="name">Name:</label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="address_1">Address line 1:</label>

                <validation-provider
                  rules="required"
                  name="address_1"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.address_1"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="phone">Phone:</label>

                <validation-provider
                  rules="required"
                  name="phone"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.phone"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="addres_2">Address line 2:</label>
                <d-input v-model="data.address_2" />
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="email">E-mail:</label>

                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.email"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label for="town">Town:</label>

                <validation-provider
                  rules="required"
                  name="town"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.town"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label for="notes">Notes:</label>
                <d-textarea
                  v-model="data.notes"
                  :value.prop="data.notes"
                  :rows="10" />
              </div>
            </d-col>

            <d-col cols="6" class="px-3">
              <d-row>
                <d-col cols="4">
                  <div class="form-group">
                    <label for="post_code">Post code:</label>

                    <validation-provider
                      rules="required"
                      name="post_code"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.post_code"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="8">
                  <div class="form-group">
                    <label for="country_id">Country:</label>

                    <validation-provider
                      rules="required"
                      name="country_id"
                      v-slot="{ errors }">
                      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                        <v-select
                          :options="countriesOptions"
                          v-model="data.country_id"
                          :reduce="country => country.id"
                          placeholder="Select a country"
                          label="text">
                          <template slot="no-options">
                            There are no countries for the provided phrase&hellip;
                          </template>
                        </v-select>
                      </div>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="12">
              <map-selector
                :latitude="parseFloat(data.latitude)"
                :longitude="parseFloat(data.longitude)"
                @locationUpdated="locationUpdated" />
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>
    </d-container>
  </div>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import ListUtils from './../../utils/ListUtils.js';
import MapSelector from './../common/MapSelector';

export default {
  name: 'hotel-form',
  mixins: [
    HasChangesDetection
  ],
  components: {
    'map-selector': MapSelector
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCountries;
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new hotel';
      }

      return 'Edit hotel';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      return 'Delete hotel';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        name: '',
        phone: '',
        email: '',
        address_1: '',
        address_2: '',
        town: '',
        post_code: '',
        country_id: null,
        notes: '',
        latitude: 51.509865,
        longitude: 0
      },
      countries: [],
      loadError: false,
      loadedData: false,
      loadedCountries: false,
      removeInProgress: false,
      saveInProgress: false
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
      this.loadAdditionalData();
    } else {
      this.loadData();
    }

    this.$bus.$on('hotel-item-delete', this.removeHotel);
    this.$bus.$on('hotel-item-save', this.validateForm);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/hotels/item/',
        instanceFields: ['id', 'name', 'phone', 'email', 'address_1', 'address_2', 'town', 'post_code', 'country_id', 'notes', 'latitude', 'longitude'],
        loadedKey: 'loadedData'
      });

      this.loadAdditionalData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/countries/items',
        params: {
          pagination: 0
        },
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });
    },
    removeHotel () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/hotels/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'Hotel removed',
        successText: 'The selected hotel has been removed',
        confirmTitle: 'Do you really want to remove this hotel?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/hotels');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['hotel-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = '/hotels/store';
      let mode = 'add';

      if (this.id > 0) {
        endpointUrl = '/hotels/update/' + parseInt(this.id, 10);
        mode = 'edit';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'Hotel added' : 'Hotel saved',
        successText: 'The hotel has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/hotels');
        }
      });
    },
    locationUpdated (latlng) {
      this.data.latitude = latlng.lat;
      this.data.longitude = latlng.lng;
    }
  },
  beforeDestroy () {
    this.$bus.$off('hotel-item-delete', this.removeHotel);
    this.$bus.$off('hotel-item-save', this.validateForm);
  }
}
</script>
